<script setup>
import { valueProps } from '@/data/catalog'
import WhatsappButton from '@/components/WhatsappButton.vue'
import QuoteButton from '@/components/QuoteButton.vue'

defineProps({
  phone: { type: String, required: true }
})
</script>

<template>
  <section class="relative isolate flex min-h-[88svh] flex-col justify-end overflow-hidden bg-night">
    <!-- The photo is the argument: what is being sold is the light coming in. -->
    <img
      src="/images/roller-screen-terraza.jpg"
      alt="Terraza cerrada con cortinas roller screen encendidas por el sol de la tarde"
      width="1280"
      height="960"
      class="hero-photo absolute inset-0 -z-10 h-full w-full object-cover"
      fetchpriority="high"
    />

    <div
      class="absolute inset-0 -z-10 bg-linear-to-t from-night via-night/70 to-night/25"
      aria-hidden="true"
    ></div>
    <!-- Copy lives on the left; this scrim gives it a ground without dimming
         the luminous half of the photo, which is the product. -->
    <div
      class="absolute inset-0 -z-10 bg-linear-to-r from-night/80 via-night/20 via-38% to-transparent to-62%"
      aria-hidden="true"
    ></div>
    <div
      class="absolute inset-x-0 bottom-0 -z-10 h-32 bg-linear-to-b from-transparent to-night"
      aria-hidden="true"
    ></div>

    <div class="mx-auto w-full max-w-7xl px-6 pb-12 pt-28 lg:px-10 lg:pb-16">
      <h1
        class="hero-rise display mt-5 max-w-4xl text-balance text-[clamp(2.9rem,8.5vw,7rem)] text-linen"
        style="--delay: 220ms"
      >
        La luz, a tu medida.
      </h1>

      <p
        class="hero-rise mt-5 max-w-md text-lg leading-snug text-linen/75"
        style="--delay: 340ms"
      >
        Cortinas roller, verticales y persianas, fabricadas al milímetro de tu
        ventana.
      </p>

      <!-- Los dos canales, juntos y con el mismo peso: el formulario relleno y
           WhatsApp en contorno. Antes solo estaba WhatsApp y el correo no se
           veía desde arriba. -->
      <div
        class="hero-rise mt-8 flex flex-wrap items-center gap-x-4 gap-y-4"
        style="--delay: 440ms"
      >
        <QuoteButton size="large" label="Pedir cotización" source="hero" />

        <WhatsappButton
          :phone="phone"
          variant="outline"
          size="large"
          label="WhatsApp"
          subject="cotizar cortinas a medida para mi casa"
          source="hero"
        />

        <a
          href="#productos"
          class="group -my-3 ml-2 inline-flex items-center gap-2 py-3 text-linen/80 transition-colors hover:text-glow"
        >
          Ver las cortinas
          <span class="transition-transform duration-300 group-hover:translate-y-0.5">↓</span>
        </a>
      </div>

      <!-- The three reasons to write, before any scrolling. -->
      <ul
        class="hero-rise mt-10 flex flex-wrap gap-x-7 gap-y-2 border-t border-linen/15 pt-6 text-linen/75"
        style="--delay: 560ms"
      >
        <li v-for="claim in valueProps" :key="claim" class="flex items-center gap-2">
          <span class="h-1 w-1 rounded-full bg-glow" aria-hidden="true"></span>
          {{ claim }}
        </li>
      </ul>
    </div>
  </section>
</template>

<style scoped>
.hero-photo {
  animation: zoom-out 2400ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

.hero-rise {
  animation: rise 900ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
  animation-delay: var(--delay, 0ms);
}

@keyframes zoom-out {
  from {
    transform: scale(1.08);
  }
  to {
    transform: scale(1);
  }
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(1.25rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-photo,
  .hero-rise {
    animation: none;
  }
}
</style>
